/* ============================================================
   makerWorldPrefillView — MW-3 (TASK-40064910): pure view model
   for the MakerWorld link → prefill card. Maps the MW-2 endpoint
   payload (POST /api/v1/quotes/prefill-from-link) onto render-ready
   rows: preview, profile selector, confidence chips, editable
   grams/minutes, material hint, and the §5.4 fallback notice.
   Every rule here mirrors the backend MakerWorldPrefillService
   (1ca5f3cf) so the two sides never disagree:
   - minutes = Math.Round(seconds / 60.0, AwayFromZero);
   - dimensions/layer height are always «введите вручную» (no bbox upstream);
   - found:false is a graceful fallback, never an error.
   Pure functions only — no React, no fetch. Node-testable.
   Light theme, Montserrat, iOS-18 minimal per DESIGN-SYSTEM.md.
   ============================================================ */

import type {
  MakerWorldMaterialHintDto,
  MakerWorldPrefillDto,
  MakerWorldPrefillResultDto,
  MakerWorldProfileDto,
  Material,
  PrefillConfidenceDto,
} from '../types';

/* ── Confidence chips (spec §5.2: точно / приблизительно / введите вручную) ── */

export type PrefillChipKind = 'exact' | 'approx' | 'manual';

export const CHIP_LABELS: Record<PrefillChipKind, string> = {
  exact: 'точно',
  approx: 'приблизительно',
  manual: 'введите вручную',
};

/** Confidence number/string → the three user-facing chip kinds. */
export function chipKindFor(confidence: PrefillConfidenceDto | null | undefined): PrefillChipKind {
  const c = confidence === null || confidence === undefined ? 0 : confidence;
  const v = typeof c === 'number' ? c : ({ None: 0, Low: 1, Medium: 2, High: 3 } as Record<string, number>)[c] ?? 0;
  return v >= 3 ? 'exact' : v >= 1 ? 'approx' : 'manual';
}

/** A prefillable value plus its chip kind. value null ⇒ manual input. */
export interface PrefillValueView {
  value: number | null;
  chip: PrefillChipKind;
}

/* ── Preview + fields ─────────────────────────────────────── */

export interface MakerWorldPreviewView {
  title: string;
  creator: string | null;
  coverUrl: string | null;
  license: string | null;
}

export interface MakerWorldProfileView {
  id: number;
  /** Upstream name or a stable fallback «Профиль N». */
  label: string;
  hasValues: boolean;
}

export interface MakerWorldMaterialView {
  /** Raw upstream filament type (shown even when unmapped, spec §2.2). */
  label: string;
  chip: PrefillChipKind;
  colorHex: string | null;
  /** Best-effort catalog type; used to preselect a quote material. */
  catalogType: string | null;
}

export interface MakerWorldPrefillView {
  preview: MakerWorldPreviewView;
  profiles: MakerWorldProfileView[];
  selectedProfileId: number | null;
  grams: PrefillValueView;
  minutes: PrefillValueView;
  /** No bbox upstream (spec §2.3) — informational row, always manual. */
  dimensionsChip: PrefillChipKind;
  material: MakerWorldMaterialView | null;
  hasMultipleProfiles: boolean;
}

/** Minutes rounding mirrors the backend (AwayFromZero for positive values). */
export function secondsToMinutes(seconds: number): number {
  return Math.round(seconds / 60);
}

function profileLabel(p: MakerWorldProfileDto): string {
  const name = (p.name ?? '').trim();
  return name.length > 0 ? name : `Профиль ${p.id}`;
}

/** Field pair for the currently selected profile: backend-resolved values for
 *  the backend-selected profile (may include plate-sum), recomputed from the
 *  profile summary for any other profile; missing data → manual input.
 *  Backend selection is `selectedProfileId ?? first profile` (MW-2 precedence
 *  #profileId → default → first) — clicking that pill keeps backend values. */
export function fieldsForProfile(
  dto: MakerWorldPrefillDto,
  profileId: number | null,
): { grams: PrefillValueView; minutes: PrefillValueView } {
  const effectiveSelected = dto.selectedProfileId ?? dto.profiles[0]?.id ?? null;
  if (profileId === null || profileId === effectiveSelected) {
    return {
      grams: { value: dto.grams?.value ?? null, chip: chipKindFor(dto.grams?.confidence ?? 0) },
      minutes: { value: dto.minutes?.value ?? null, chip: chipKindFor(dto.minutes?.confidence ?? 0) },
    };
  }
  const profile = dto.profiles.find((p) => p.id === profileId) ?? null;
  return {
    grams: { value: profile?.weightGrams ?? null, chip: profile?.weightGrams != null ? 'approx' : 'manual' },
    minutes: {
      value: profile?.predictionSeconds != null ? secondsToMinutes(profile.predictionSeconds) : null,
      chip: profile?.predictionSeconds != null ? 'approx' : 'manual',
    },
  };
}

/** Builds the full card view from a found:true payload. */
export function buildMakerWorldPrefillView(dto: MakerWorldPrefillDto): MakerWorldPrefillView {
  const selectedId = dto.selectedProfileId ?? dto.profiles[0]?.id ?? null;
  const fields = fieldsForProfile(dto, selectedId);

  // Dominant hint first (backend orders by used grams); shown raw when unmapped.
  const hint: MakerWorldMaterialHintDto | null = dto.materialHints?.[0] ?? null;

  return {
    preview: {
      title: (dto.title ?? '').trim() || `Модель ${dto.modelId}`,
      creator: (dto.creatorName ?? '').trim() || null,
      coverUrl: dto.coverUrl ?? null,
      license: dto.license ?? null,
    },
    profiles: (dto.profiles ?? []).map((p) => ({
      id: p.id,
      label: profileLabel(p),
      hasValues: p.weightGrams != null || p.predictionSeconds != null,
    })),
    selectedProfileId: selectedId,
    grams: fields.grams,
    minutes: fields.minutes,
    dimensionsChip: 'manual',
    material: hint
      ? {
          label: hint.filamentType?.trim() || 'Неизвестный материал',
          chip: chipKindFor(dto.materialConfidence ?? 0),
          colorHex: hint.colorHex ?? null,
          catalogType: hint.catalogType != null ? materialTypeKey(hint.catalogType) : null,
        }
      : null,
    hasMultipleProfiles: (dto.profiles ?? []).length > 1,
  };
}

/* ── Material preselect ───────────────────────────────────── */

const MATERIAL_TYPE_KEYS: Record<number, string> = {
  0: 'Pla', 1: 'Petg', 2: 'Abs', 3: 'Tpu', 4: 'ResinStandard', 5: 'ResinEngineering', 6: 'Nylon',
};

/** Enum arrives as a number (default serializer); accept the string form too. */
export function materialTypeKey(catalogType: Material | number | string): string | null {
  if (typeof catalogType === 'number') return MATERIAL_TYPE_KEYS[catalogType] ?? null;
  const s = String(catalogType);
  return MATERIAL_TYPE_KEYS[Number(s)] ?? s;
}

function sameType(a: string | null, b: string | null): boolean {
  return a !== null && b !== null && a.toLowerCase() === b.toLowerCase();
}

/** First active catalog material matching the hint's type (spec §5.2 preselect). */
export function resolveMaterialId(view: MakerWorldPrefillView, materials: Material[]): string | null {
  const catalogType = view.material?.catalogType ?? null;
  if (!catalogType) return null;
  const match = materials.find((m) => m.isActive !== false && sameType(materialTypeKey(m.type), catalogType));
  return match?.id ?? null;
}

/* ── §5.4 fallback — manual input, the form lives exactly as today ── */

export interface PrefillFallbackView {
  /** The spec's single user-facing line for every miss reason. */
  title: string;
  /** Short reason-specific explanation (secondary color). */
  hint: string;
}

const FALLBACK_HINTS: Record<string, string> = {
  'invalid-link': 'Ссылка не похожа на страницу модели MakerWorld',
  disabled: 'Автоподстановка по ссылкам сейчас отключена',
  'not-found': 'Модель не найдена — возможно, она удалена или приватная',
  unavailable: 'MakerWorld не ответил — попробуйте позже',
  'paid-model': 'Платные модели не поддерживаются',
  'not-printable': 'Эта модель недоступна для печати',
  'no-print-profiles': 'У модели нет профилей печати',
};

export const FALLBACK_TITLE = 'Данные по ссылке не получены — введите параметры вручную';
export const FALLBACK_HINT_DEFAULT = 'Проверьте ссылку или заполните параметры формы вручную';
export const FALLBACK_HINT_NETWORK = 'Не удалось связаться с сервисом — проверьте подключение и попробуйте ещё раз';

export function fallbackNotice(reason: string | null | undefined, networkError = false): PrefillFallbackView {
  if (networkError) return { title: FALLBACK_TITLE, hint: FALLBACK_HINT_NETWORK };
  const hint = reason != null && FALLBACK_HINTS[reason] != null ? FALLBACK_HINTS[reason] : FALLBACK_HINT_DEFAULT;
  return { title: FALLBACK_TITLE, hint };
}

/* ── Result wrapper ───────────────────────────────────────── */

export type MakerWorldPrefillOutcome =
  | { status: 'found'; view: MakerWorldPrefillView; dto: MakerWorldPrefillDto }
  | { status: 'fallback'; fallback: PrefillFallbackView };

/** found:false / network failure → fallback (never an error, spec §5.4). */
export function prefillOutcome(
  result: MakerWorldPrefillResultDto | null,
  networkError = false,
): MakerWorldPrefillOutcome {
  if (result !== null && result.found && result.prefill !== null) {
    return { status: 'found', view: buildMakerWorldPrefillView(result.prefill), dto: result.prefill };
  }
  return { status: 'fallback', fallback: fallbackNotice(result?.reason ?? null, networkError) };
}
